import { formatFinancialValue } from '@/shared/components/financial-values';
import { DatasetQuery } from '@/types/api/modules/business-intelligence';
import type { FinancialValueDisplayPreference } from '@/types/financialValueDisplay';

export type FinancialChartValueFormat = 'currency' | 'percentage' | 'multiple' | 'days' | 'number';
export type ChartValueFormat = FinancialChartValueFormat | 'date' | 'string' | 'boolean';

type FormatFinancialChartValueArgs = {
    preference?: FinancialValueDisplayPreference;
    value: number | string | null | undefined;
    format: FinancialChartValueFormat;
};

type FormatChartValueArgs = {
    preference?: FinancialValueDisplayPreference;
    value: number | string | boolean | null | undefined;
    format: ChartValueFormat;
};

type ResolveFinancialValueFormatArgs = {
    datasetId?: string;
    metricField?: string;
    metricType?: string;
};

type ResolveChartValueFormatArgs = ResolveFinancialValueFormatArgs & {
    fieldType?: string;
};

const accountingCurrencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const multipleFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const daysFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
});

const percentLikeMetricPattern = /(margin|rate|yield|return|payout|capitalization|coverage|debtRatio|debtEquityRatio)/i;
const dayMetricPattern = /(days|cycle)/i;
const multipleMetricPattern =
    /(multiple|price|earnings|book|sales|cashflow|cashflows|turnover|ratio|perShare|multiplier)/i;

const toNumber = (value: FormatFinancialChartValueArgs['value']) => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }

    const numeric = Number(value);

    return Number.isFinite(numeric) ? numeric : undefined;
};

const formatAccountingCurrency = (value: number) => {
    const formatted = accountingCurrencyFormatter.format(Math.abs(value));

    return value < 0 ? `(${formatted})` : formatted;
};

export const resolveFinancialValueFormat = ({
    datasetId,
    metricField,
    metricType,
}: ResolveFinancialValueFormatArgs): FinancialChartValueFormat => {
    if (metricType === 'currency') {
        return 'currency';
    }
    if (metricType === 'percentage' || datasetId === 'returns') {
        return 'percentage';
    }

    if (datasetId === 'multiples' || datasetId === 'ratios') {
        if (metricField && dayMetricPattern.test(metricField)) {
            return 'days';
        }
        if (metricField && percentLikeMetricPattern.test(metricField)) {
            return 'percentage';
        }
        if (metricField && multipleMetricPattern.test(metricField)) {
            return 'multiple';
        }

        return 'number';
    }

    return 'number';
};

export const formatFinancialChartValue = ({ value, format, preference }: FormatFinancialChartValueArgs) => {
    const numeric = toNumber(value);

    if (numeric === undefined) {
        return '';
    }

    switch (format) {
        case 'currency':
            return preference ? formatFinancialValue(numeric, preference) : formatAccountingCurrency(numeric);

        case 'percentage':
            return percentFormatter.format(numeric);

        case 'multiple':
            return `${multipleFormatter.format(numeric)}x`;

        case 'days':
            return `${daysFormatter.format(numeric)} days`;

        case 'number':
            return preference
                ? formatFinancialValue(numeric, preference, { kind: 'number' })
                : decimalFormatter.format(numeric);

        default:
            return decimalFormatter.format(numeric);
    }
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

export const resolveChartValueFormat = ({
    datasetId,
    metricField,
    metricType,
    fieldType,
}: ResolveChartValueFormatArgs): ChartValueFormat => {
    if (fieldType === 'date') {
        return 'date';
    }
    if (fieldType === 'boolean') {
        return 'boolean';
    }
    if (fieldType === 'string' || fieldType === 'enum') {
        return 'string';
    }

    return resolveFinancialValueFormat({ datasetId, metricField, metricType });
};

export const formatChartPrimitive = ({ value, format, preference }: FormatChartValueArgs) => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    switch (format) {
        case 'date': {
            const date = new Date(String(value));

            return Number.isNaN(date.getTime()) ? String(value) : dateFormatter.format(date);
        }

        case 'boolean':
            return value ? 'Yes' : 'No';

        case 'string':
            return String(value);

        default:
            return formatFinancialChartValue({
                value: value as number | string,
                format,
                preference,
            });
    }
};

export const formatChartValue = (value: number, metric: any, preference?: FinancialValueDisplayPreference) =>
    formatFinancialChartValue({
        preference,
        value,
        format: resolveFinancialValueFormat({
            datasetId: metric?.datasetId,
            metricField: metric?.field,
            metricType: metric?.type ?? metric?.format,
        }),
    });

export const createFormatter = (metric: any, preference?: FinancialValueDisplayPreference) => {
    const format = resolveFinancialValueFormat({
        datasetId: metric?.datasetId,
        metricField: metric?.field,
        metricType: metric?.type ?? metric?.format,
    });

    return (value: number) => formatFinancialChartValue({ value, format, preference });
};

export const createChartFieldFormatter = (
    field: any,
    datasetId?: string,
    preference?: FinancialValueDisplayPreference,
) => {
    const format = resolveChartValueFormat({
        datasetId,
        metricField: field?.field ?? field?.key,
        metricType: field?.type ?? field?.format,
        fieldType: field?.type,
    });

    return (value: number | string | boolean | null | undefined) => formatChartPrimitive({ value, format, preference });
};

const humanize = (field: string) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const buildAxes = (query: DatasetQuery) => {
    const dimension = query.dimensions[0];
    const metric = query.metrics[0];

    const formatter = createFormatter(metric);

    return {
        bottom: {
            legend: humanize(dimension.field),
            tickRotation: 0,
            format: formatter,
        },
        left: {
            legend: humanize(metric.field),
            format: formatter,
        },
    };
};

// export const getCommonChartProps = (query, theme) => ({
//     margin: { top: 32, right: 24, bottom: 48, left: 64 },
//     theme,
//     animate: false,
// });
